import { Colors } from '../../../theme';
import { Avatar } from '../../display/Avatar';
import styles from './TestimonialMultipleColumn.module.scss';
import {
  BackgroundColors,
  remapDeprecatedColors,
} from '../TestimonialSolo/TestimonialMapping';

import {
  TestimonialSoloProps as TestimonialProps,
  TestimonialSolo,
} from '../TestimonialSolo';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

export type Props = {
  /**
   * Array of testimonial elements.
   * Fields:
   *
   * `quote`: Text content displayed on the top or below the avatar.
   *
   * `author`: Text content displayed below quote.
   *
   * `company`: Text content displayed below author initials.
   *
   * `hasAuthorAvatar?`: Whether or not Testimonial has author avatar. Default value is `False`.
   *
   * `avatarUrl`: Author avatar url. This field is not required.
   */
  testimonials: Array<Omit<TestimonialProps, 'backgroundColor'>>;
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava10' | 'ocean10' | 'sun10' | 'white' | 'cream' | 'neutral100' | 'acidWash' | 'neutral200'.
   */
  backgroundColor?: BackgroundColors;
  /**
   * Number of columns to be displayed.
   */
  numberOfColumns: 2 | 3;
};

const renderContentForTwoColumn = (props: Props, index: number) => {
  return (
    <div className={styles.twoColumnContent} data-zds>
      {props.testimonials[index].hasAuthorAvatar && (
        <div className={styles.twoColumnAvatarContainer} data-zds>
          <Avatar
            data-testId="Avatar"
            name={props.testimonials[index].author}
            url={props.testimonials[index].avatarUrl}
            size="medium"
          />
        </div>
      )}
      <div className={styles.quote} data-zds>
        {props.testimonials[index].quote}
      </div>
      <p className={styles.author} data-zds>
        {props.testimonials[index].author}
      </p>
      <p className={styles.company} data-zds>
        {props.testimonials[index].company}
      </p>
    </div>
  );
};

const renderTwoColumn = (props: Props) => {
  return (
    <div
      className={styles.twoColumnOuterBox}
      style={{
        '--_zds-testimonialcolumn-background-color':
          Colors[
            LegacyColorMap[
              remapDeprecatedColors(props.backgroundColor || 'neutral100')
            ]
          ],
      }}
      data-zds
    >
      <div className={styles.twoColumnGrid} data-zds>
        {renderContentForTwoColumn(props, 0)}
        <div className={styles.twoColumnBorder} data-zds />
        {renderContentForTwoColumn(props, 1)}
      </div>
    </div>
  );
};

const renderThreeColumn = (props: Props) => {
  return (
    <div
      className={styles.threeColumnOuterBox}
      style={{
        '--_zds-testimonialcolumn-background-color':
          Colors[
            LegacyColorMap[
              remapDeprecatedColors(props.backgroundColor || 'neutral100')
            ]
          ],
      }}
      data-zds
    >
      <div className={styles.threeColumnGrid} data-zds>
        {props.testimonials
          .slice(0, props.numberOfColumns)
          .map((testimonial, index) => (
            <div key={index} className={styles.threeColumnContent} data-zds>
              <div className={styles.quote} data-zds>
                {testimonial.quote}
              </div>
              {testimonial.hasAuthorAvatar ? (
                <div className={styles.threeColumnInnerFlex} data-zds>
                  <div className={styles.twoColumnAvatarContainer} data-zds>
                    <Avatar
                      data-testId="Avatar"
                      name={testimonial.author}
                      url={testimonial.avatarUrl}
                      size="medium"
                    />
                  </div>
                  <div data-zds>
                    <p className={styles.author} data-zds>
                      {testimonial.author}
                    </p>
                    <p className={styles.company} data-zds>
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ) : (
                <div data-zds>
                  <p className={styles.author} data-zds>
                    {testimonial.author}
                  </p>
                  <p className={styles.company} data-zds>
                    {testimonial.company}
                  </p>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

/**
 * `TestimonialMulipleColumn` is used to add testimonials from happy customers to our landing pages. The number of columns to be displayed can be either 2 or 3. Each column features a quote text, an author initials, company and avatar.
 */
export const TestimonialMultipleColumn = (props: Props) => {
  /*
    If the the length of testimonial array is 1, we are rendering Solo Testimonial.
  */
  if (props.testimonials.length === 1) {
    const testimonial = props.testimonials[0];
    return (
      <TestimonialSolo
        backgroundColor={props.backgroundColor}
        avatarUrl={testimonial.avatarUrl}
        company={testimonial.company}
        quote={testimonial.quote}
        author={testimonial.author}
      />
    );
  }
  const renderType =
    props.numberOfColumns === 2 ? renderTwoColumn : renderThreeColumn;
  return renderType(props);
};
